/**
 * Response Tracker Service
 * Tracks API response times and success rates for analytics
 */

interface ResponseMetric {
  endpoint: string;
  method: string;
  statusCode: number;
  duration: number;
  timestamp: number;
  success: boolean;
}

interface AggregatedMetrics {
  totalRequests: number;
  successRate: number;
  averageLatency: number;
  p95Latency: number;
  errorsByEndpoint: Record<string, number>;
}

class ResponseTrackerService {
  private metrics: ResponseMetric[] = [];
  private maxMetrics = 1000;
  private storageKey = 'response_metrics';

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.metrics = JSON.parse(stored);
      }
    } catch {
      // Use empty array
    }
  }

  private saveToStorage(): void {
    try {
      // Keep only recent metrics
      const recent = this.metrics.slice(-this.maxMetrics);
      localStorage.setItem(this.storageKey, JSON.stringify(recent));
    } catch {
      // Storage full - clear old data
      this.metrics = this.metrics.slice(-this.maxMetrics / 2);
    }
  }

  track(metric: Omit<ResponseMetric, 'timestamp'>): void {
    this.metrics.push({
      ...metric,
      timestamp: Date.now(),
    });

    // Periodic cleanup
    if (this.metrics.length > this.maxMetrics * 1.5) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }

    // Debounced save
    this.debouncedSave();
  }

  private saveTimeout: NodeJS.Timeout | null = null;
  private debouncedSave(): void {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => this.saveToStorage(), 5000);
  }

  // Get metrics for a specific time range (default: last hour)
  getMetrics(timeRangeMs: number = 60 * 60 * 1000): ResponseMetric[] {
    const cutoff = Date.now() - timeRangeMs;
    return this.metrics.filter(m => m.timestamp > cutoff);
  }

  // Get aggregated metrics
  getAggregated(timeRangeMs: number = 60 * 60 * 1000): AggregatedMetrics {
    const recent = this.getMetrics(timeRangeMs);
    
    if (recent.length === 0) {
      return {
        totalRequests: 0,
        successRate: 1,
        averageLatency: 0,
        p95Latency: 0,
        errorsByEndpoint: {},
      };
    }

    const successful = recent.filter(m => m.success);
    const latencies = recent.map(m => m.duration).sort((a, b) => a - b);
    const p95Index = Math.floor(latencies.length * 0.95);

    const errorsByEndpoint: Record<string, number> = {};
    recent.filter(m => !m.success).forEach(m => {
      errorsByEndpoint[m.endpoint] = (errorsByEndpoint[m.endpoint] || 0) + 1;
    });

    return {
      totalRequests: recent.length,
      successRate: successful.length / recent.length,
      averageLatency: latencies.reduce((a, b) => a + b, 0) / latencies.length,
      p95Latency: latencies[p95Index] || 0,
      errorsByEndpoint,
    };
  }

  // Clear all metrics
  clear(): void {
    this.metrics = [];
    localStorage.removeItem(this.storageKey);
  }

  // Helper to create a tracking wrapper
  createTracker<T>(
    endpoint: string,
    method: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    const start = performance.now();
    
    return requestFn()
      .then(result => {
        this.track({
          endpoint,
          method,
          statusCode: 200,
          duration: performance.now() - start,
          success: true,
        });
        return result;
      })
      .catch(error => {
        this.track({
          endpoint,
          method,
          statusCode: error.statusCode || 500,
          duration: performance.now() - start,
          success: false,
        });
        throw error;
      });
  }
}

export const responseTracker = new ResponseTrackerService();
